import { useState } from 'react';
import { useSelector } from 'react-redux';
import Image from 'next/image';
import Link from 'next/link';
import moment from 'moment';
import ImageWithFallback from '@/Component/Common/ImageWithFallback';
import { InfluencerGoogleSVG, InfluencerInstagramSVG, InfluencerTiktokSVG, XiaohongshuSVG } from '@/Component/Common/SVGs/CommonSVG';
import SkeletonLoader from '@/Layout/SkeletonLoader';
import { formatNumber } from '@/Utils/Other/FormateNumbers';
import { FacebookSVG, YoutubeSVG } from '../SVGs/AuthSVG';
import HeartIcon from '@/assets/images/heart.png';
import CommentIcon from '@/assets/images/comment.png';
import NoData from '../Tables/NoData/NoData';
import FavoriteToggle from './FavoriteToggle';
import { AppStore } from '@/lib/store';
import { useRouter } from 'next/navigation';

const InfluencerBox = (props) => {
    const { data, skeletonLoading, listingData, index, setOpenSideModal, favoriteList, setFavoriteList, translations, isMock, setModal, isFromWithoutAuth = false } = props;
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [isFavorite, setIsFavorite] = useState(data?.favoritesCount === 1 ? true : false);
    const userDetails: any = useSelector((state: AppStore) => state.userDetailSlice?.userDetails);
    const router = useRouter();

    const handleBoxClick = (event, data) => {
        if (isFromWithoutAuth) {
            router.push('/influencers/' + data?.slug);
            return;
        }
        if (isMock) {
            setModal(true);
            return;
        }
        if (event.target.tagName === 'BUTTON' || event.target.tagName === 'A' || event.target.closest('button') || event.target.closest('a')) {
            return;
        }
        event.stopPropagation();
        setOpenSideModal(data);
    };

    const getSocialMediaLink = (type: string) => {
        const media = data?.social_medias?.find((m) => m?.social_type === type);
        if (!media) return '#';

        switch (type) {
            case 'instagram':
                return `https://www.instagram.com/${media.social_user_name}`;
            case 'facebook':
                return `https://www.facebook.com/${media.social_user_name}`;
            case 'tiktok':
                return `https://www.tiktok.com/@${media.social_user_name}`;
            case 'youtube':
                return `https://www.youtube.com/@${media.social_user_name}`;
            case 'xiaohongshu':
                return `https://www.xiaohongshu.com/user/profile/${media.social_user_name}`;
            default:
                return '#';
        }
    };

    return (
        <>
            {skeletonLoading ?
                <SkeletonLoader width="378" height={154} rows={1} /> :
                <div data-sidebar-trigger className={`${listingData ? 'justify-between flex-col lg:flex-row gap-3 w-full' : 'flex-col gap-7'} group border border-neutral-100 rounded-card p-4 flex cursor-pointer hover:border-primary/20 hover:shadow-float hover:-translate-y-0.5 bg-white shadow-soft transition-all duration-200 ${isMock ? 'blur-sm' : ''} ${isFromWithoutAuth && isMock ? 'cursor-default pointer-events-none' : ''}`} onClick={(event) => handleBoxClick(event, data)} >
                    <div className={`flex  gap-2.5 ${listingData ? "lg:w-1/5 items-start" : "w-auto items-center"}`}>
                        <div className='min-w-16 w-16 h-16 rounded-full overflow-hidden bg-gray'>
                            <ImageWithFallback src={data?.image} alt='Profile' className='w-full h-full object-cover' height={70} width={70} />
                        </div>
                        <div className='flex flex-col'>
                            <h5 className='font-semibold text-base leading-6 line-clamp-1'>{data?.display_name}</h5>
                            <div className='flex items-center gap-2'>
                                <span className='text-xs leading-6'>{data?.country}</span>
                                {data?.influencerCategories?.length > 0 && <div className='w-1 h-1 bg-gray rounded-full'></div>}

                                {(() => {
                                    const nonNullCategories = data?.influencerCategories?.filter(cat => cat?.category !== null) || [];
                                    return (
                                        nonNullCategories.length > 0 && (
                                            <>
                                                <span className={`${nonNullCategories.length > 1 ? 'line-clamp-1 max-2xl:max-w-44 2xl:max-w-40' : 'line-clamp-1 w-full'} text-xs`}>
                                                    {nonNullCategories[0]?.category?.translations?.[0]?.title}
                                                </span>
                                                {nonNullCategories.length > 1 && (
                                                    <span
                                                        className='relative text-primary underline cursor-pointer whitespace-nowrap text-xs'
                                                        onMouseEnter={() => setHoveredIndex(index)}
                                                        onMouseLeave={() => setHoveredIndex(null)}
                                                    >
                                                        {` +${nonNullCategories.length - 1} more`}
                                                        {hoveredIndex === index && (
                                                            <div className="absolute top-full right-0 bg-white text-black p-2 rounded shadow-lg w-max z-10 text-xs">
                                                                {nonNullCategories.slice(1).map((category, idx) => (
                                                                    <div key={idx} className="text-xs">{category?.category?.translations?.[0]?.title}</div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </span>
                                                )}
                                            </>
                                        )
                                    );
                                })()}


                            </div>
                            {listingData ?
                                <>
                                    <div className="flex gap-5 mt-4">
                                        {data?.platforms?.length > 0 ?
                                            <>
                                                {data?.platforms.includes("instagram") ? <Link target='_blank' href={getSocialMediaLink('instagram')}><InfluencerInstagramSVG /></Link> : null}
                                                {data?.platforms.includes("facebook") ? <Link target='_blank' href={getSocialMediaLink('facebook')}><FacebookSVG /></Link> : null}
                                                {data?.platforms.includes("tiktok") ? <Link target='_blank' href={getSocialMediaLink('tiktok')}><InfluencerTiktokSVG /></Link> : null}
                                                {data?.platforms.includes("youtube") ? <Link target='_blank' href={getSocialMediaLink('youtube')}><YoutubeSVG /></Link> : null}
                                                {data?.platforms.includes("xiaohongshu") ? <Link target='_blank' href={getSocialMediaLink('xiaohongshu')}><XiaohongshuSVG /></Link> : null}
                                            </>
                                            : null}
                                    </div>
                                </> : ''
                            }
                        </div>
                    </div>
                    <div className={`flex items-center justify-between ${listingData ? 'gap-4 2xl:gap-14 flex-col !items-start lg:flex-row' : ''}`}>
                        {listingData ?
                            <>
                                <div className="grid grid-cols-3 gap-3 w-full lg:w-auto">
                                    {
                                        data?.socialMediaPosts?.length > 0 ?
                                            data?.socialMediaPosts?.map((post) => (
                                                <div className='relative w-20 h-20 '>
                                                    <ImageWithFallback src={post?.image_url ? post?.image_url : null} alt='Brand' width={70} height={70} className='object-cover rounded w-full h-full ' />
                                                    <div className="content absolute bottom-1 left-1 hidden xl:block">
                                                        <div>
                                                            <p className='mb-0.5 inline-block bg-black bg-opacity-60 text-white py-0.5 px-1 rounded text-[8px] '>{post?.created_time ? moment(post?.created_time).format("MMMM DD, yyy") : null}</p>
                                                            <div className="flex gap-1 bg-black bg-opacity-60 rounded text-white py-0.5 px-1">
                                                                <p className='flex gap-1 items-center'>
                                                                    <Image src={HeartIcon} alt='Heart' width={8} height={8} className='max-h-2' />
                                                                    <span className='text-[8px] block'>{formatNumber(post?.like_count)}</span>
                                                                </p>
                                                                <p className='flex gap-1 items-center'>
                                                                    <Image src={CommentIcon} alt='Heart' width={8} height={8} className='max-h-2' />
                                                                    <span className='text-[8px] block'>{formatNumber(post?.comment_count)}</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                            : <div className='relative col-span-12 md:col-span-4'>{translations?.noPostFound}</div>
                                    }
                                </div>
                            </>
                            : null
                        }
                        {!listingData ?
                            <div className={`flex items-center ${listingData ? '2xl:gap-14 gap-3 ' : 'gap-7'}`}>
                                <div className='flex flex-col justify-start w-full'>
                                    <label className='font-semibold text-sm'>{formatNumber(data?.total_followers)}</label>
                                    <span className='whitespace-nowrap text-xs'>{translations?.followers}</span>
                                </div>
                                <div className='flex flex-col justify-start'>
                                    <label className='font-semibold whitespace-nowrap text-sm'>{data?.engagement_ratio == '0.00' ? '--' : Number(data?.engagement_ratio).toFixed(1)} %</label>
                                    <span className='whitespace-nowrap text-xs'>{translations?.engagementRatio}</span>
                                </div>
                            </div> : null}
                    </div>
                    <div className={`flex ${listingData ? 'justify-end gap-10' : 'justify-between'}`}>
                        {listingData ?
                            <div className={`flex items-center ${listingData ? '2xl:gap-14 gap-3 ' : 'gap-7'}`}>
                                <div className='flex flex-col justify-start w-full'>
                                    <label className='font-semibold text-sm'>{formatNumber(data?.total_followers)}</label>
                                    <span className='whitespace-nowrap text-xs'>{translations?.followers}</span>
                                </div>
                                <div className='flex flex-col justify-start'>
                                    <label className='font-semibold whitespace-nowrap text-sm'>{data?.engagement_ratio == '0.00' ? '--' : Number(data?.engagement_ratio).toFixed(1)} %</label>
                                    <span className='whitespace-nowrap text-xs'>{translations?.engagementRatio}</span>
                                </div>
                            </div> : null}
                        {!listingData ?
                            <>
                                <div className="flex gap-5">
                                    {data?.platforms?.length > 0 ?
                                        <>
                                            {data?.platforms.includes("instagram") ? <Link target='_blank' href={getSocialMediaLink('instagram')}><InfluencerInstagramSVG /></Link> : null}
                                            {data?.platforms.includes("facebook") ? <Link target='_blank' href={getSocialMediaLink('facebook')}><FacebookSVG /></Link> : null}
                                            {data?.platforms.includes("tiktok") ? <Link target='_blank' href={getSocialMediaLink('tiktok')}><InfluencerTiktokSVG /></Link> : null}
                                            {data?.platforms.includes("youtube") ? <Link target='_blank' href={getSocialMediaLink('youtube')}><YoutubeSVG /></Link> : null}
                                            {data?.platforms.includes("xiaohongshu") ? <Link target='_blank' href={getSocialMediaLink('xiaohongshu')}><XiaohongshuSVG /></Link> : null}
                                        </>
                                        : null}
                                </div>

                            </> : ``
                        }
                        {!isFromWithoutAuth && <div className="flex items-center justify-end gap-3 xl:gap-6 w-full">
                            <FavoriteToggle data={data} setIsFavorite={setIsFavorite} isFavorite={isFavorite} favoriteList={favoriteList} setFavoriteList={setFavoriteList} />
                        </div>}
                    </div>
                </div>
            }
        </>
    )
}

export default InfluencerBox